import React from "react";
import { Skull } from "lucide-react";

const clamp = (v) => Math.max(0, Math.min(5, parseInt(v, 10) || 0));

/**
 * Read-only skull skill-level display.
 * Renders 5 skulls: the first `value` are red (active), the rest are greyed out.
 * Returns null when no skill level is set (so it never shows an empty row).
 */
export function SkullRating({ value = 0, size = 18, showLabel = true, className = "" }) {
  const v = clamp(value);
  if (!v) return null;
  return (
    <div className={`flex items-center flex-wrap gap-x-2 gap-y-1 ${className}`} data-testid="skill-level" data-skill={v}>
      {showLabel && <span className="text-xs uppercase tracking-[0.18em] text-[#A19D94]">Skill Level:</span>}
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <Skull
            key={n}
            size={size}
            strokeWidth={1.75}
            className={n <= v ? "text-blood fill-blood/20" : "text-[#3a3835]"}
            data-testid={`skull-${n}-${n <= v ? "active" : "inactive"}`}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Editable skull skill-level picker (1–5). Click the same skull again to clear.
 */
export function SkullRatingInput({ value = 0, onChange }) {
  const v = clamp(value);
  return (
    <div data-testid="skill-level-input">
      <label className="text-xs uppercase tracking-[0.18em] text-[#A19D94] block mb-1.5">Skill Level Requirement</label>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange(v === n ? 0 : n)}
              data-testid={`skill-input-${n}`}
              aria-label={`Set skill level ${n}`}
              className="p-0.5 transition-transform hover:scale-110"
            >
              <Skull size={26} strokeWidth={1.5} className={n <= v ? "text-blood fill-blood/25" : "text-[#3a3835] hover:text-[#5a5550]"} />
            </button>
          ))}
        </div>
        {v > 0 && (
          <button type="button" onClick={() => onChange(0)} data-testid="skill-input-clear" className="text-[10px] uppercase tracking-wider text-[#6B6862] hover:text-blood ml-1">Clear</button>
        )}
      </div>
      <p className="text-[11px] text-[#6B6862] mt-1">{v ? `${v} of 5` : "Optional — leave blank if no minimum"}</p>
    </div>
  );
}
